import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { FaHistory } from "react-icons/fa";
import { Button } from "@/components/ui/button";
import { PageLoader } from "@/components/ui/page-loader";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useGroup } from "@/lib/context/GroupContext";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import {
  fetchActivePoll,
  fetchActivePollV2,
  fetchActivePollForHome,
  createPollV2,
  voteInPoll,
  votePollSlots,
  confirmPollSlot,
  cancelPoll,
  closePollAndCreateEvent,
  fetchGroupSettings,
  OpenPollExistsError,
  weekStartOf,
  Poll,
  PollOption,
  PollV2,
  GroupSettings,
  type ActivePollFormat,
  type CreatePollV2Input,
} from "@/lib/api/polls";
import {
  fetchWeeklyAvailabilityForDays,
  fetchUserAvailabilityDatesForDays,
  aggregateAvailabilityByDay,
  getUpcomingDays,
  AVAILABILITY_LOOKAHEAD_DAYS,
  toggleWeeklyAvailability,
  type WeeklyAvailability,
} from "@/lib/api/availability";
import {
  fetchCurrentMatchEvent,
  fetchLastCompletedMatch,
  fetchMatchById,
  fetchRsvpsForEvent,
  fetchGroupPlayers,
  ensureCurrentUserPlayerId,
  applyRsvpChange,
  clearRsvp,
  logMatchResultForEvent,
  getConfirmationProgress,
  hasPublishedLineup,
  cancelMatchEvent,
  MatchEvent,
  saveEventLineup,
  startPostKickoff,
  MatchRecord,
  CompletedMatchContext,
  PlayerRecord,
  Rsvp,
  RsvpStatus,
} from "@/lib/api/rsvp";
import { getPollWinningOption } from "@/lib/v2/pollMajority";
import { getUserPollVoteOptionId } from "@/lib/v2/pollOptions";
import {
  fetchMatchResultHistory,
  fileMatchResultDispute,
  MatchResultEdit,
} from "@/lib/api/matchResultHistory";
import { computePostMatchHighlights } from "@/lib/api/highlights";
import { dispatchNotification } from "@/lib/api/pushNotifications";
import { useEnhancedToast } from "@/hooks/use-enhanced-toast";
import { relativeEventDate } from "@/utils/relativeEventDate";
import { shareOrWhatsApp, formatTeamsShareMessage } from "@/utils/whatsappShare";
import { setupPublicRsvpPassword } from "@/lib/api/publicRsvp";
import { setupPublicPollPassword } from "@/lib/api/publicPoll";
import {
  deriveCycleShareState,
  formatPublicCycleUrl,
  fetchPublicCycleHome,
  submitPublicCyclePollVotes,
  submitPublicCycleRsvp,
  mapPublicCycleError,
  isTerminalPublicLinkStatus,
  type PublicCycleLinkStatus,
} from "@/lib/api/publicCycle";
import { PublicCycleStatusScreen } from "@/components/v2/public/PublicCycleStatusScreen";
import { usePublicCycleOptional } from "@/context/PublicCycleContext";
import { HomeCycleShareButton } from "@/components/v2/home/HomeCycleShareButton";
import { HomeWeekTracker } from "@/components/v2/home/HomeWeekTracker";
import { HomeAdminPhaseHint } from "@/components/v2/home/HomeAdminPhaseHint";
import { fetchWeatherForecast } from "@/utils/weather";
import { HomeDevPanel, type DevRoleOverride } from "@/components/v2/home/HomeDevPanel";
import { HomeAtRiskAlert } from "@/components/v2/home/HomeAtRiskAlert";
import { HomePollCard, type PollOptionVM } from "@/components/v2/home/HomePollCard";
import { HomePollVotingCard } from "@/components/v2/home/HomePollVotingCard";
import { HomeCollectingCard, type RosterEntry } from "@/components/v2/home/HomeCollectingCard";
import type { TeamPlayerVM } from "@/components/v2/home/HomeConfirmedCard";
import { HomePostKickoffCard } from "@/components/v2/home/HomePostKickoffCard";
import { HomePostMatchCard } from "@/components/v2/home/HomePostMatchCard";
import { HomeCompletedCard } from "@/components/v2/home/HomeCompletedCard";
import { HomeEventComments } from "@/components/v2/home/HomeEventComments";
import { HomeIdleCard } from "@/components/v2/home/HomeIdleCard";
import { PollCreationDialog } from "@/components/v2/home/PollCreationDialog";
import { CycleShareDialog } from "@/components/v2/home/CycleShareDialog";
import ConfirmationModal from "@/components/ConfirmationModal";
import type { LineupMember } from "@/lib/v2/lineup";
import {
  lineupMembersToTeamPlayerVM,
  parseEventLineup,
} from "@/lib/v2/lineup";
import {
  computeGuestBalanceScore,
  getGuestProfile,
  groupAverageRating,
  playerBalanceScore,
  type GuestProfileCache,
} from "@/lib/v2/guestBalance";
import type { GuestBalanceHint } from "@/lib/v2/guestBalance";
import { fetchGuestProfiles } from "@/lib/api/guestProfiles";
import type { LineupPlayer } from "@/components/v2/home/HomeLineupPanel";
import { deriveHomePhase, COMPLETED_RESET_MS } from "@/hooks/v2/useHome";
import { eventKickoffTime } from "@/lib/v2/eventPhase";

// Mock constants are used ONLY when a dev phase override is active so the
// control panel can preview each phase without live Supabase data.
const MOCK_OPTIONS = [
  { id: "opt-1", proposed_date: "2026-06-30T20:00:00Z", label: "Martes 20:00 hs", votes: 4 },
  { id: "opt-2", proposed_date: "2026-07-02T20:30:00Z", label: "Jueves 20:30 hs", votes: 7 },
];

const MOCK_LINEUP = {
  teamA: ["Agus", "Leo", "Gabi", "Manu", "Colo"],
  teamB: ["Nico", "Lucas", "Santi", "Tomi", "Invitado 1"],
};

const MOCK_ROSTER = {
  going: ["Agus", "Leo", "Gabi", "Manu", "Colo", "Nico", "Lucas"],
  maybe: ["Santi", "Tomi"],
  out: [] as string[],
  reserve: [] as string[],
};

const MOCK_LINEUP_EVENT_ID = "mock-convocatoria-event";

const MOCK_RECAP = { scoreA: 5, scoreB: 3 };

export default function HomePage() {
  const { t } = useTranslation();
  const publicCtx = usePublicCycleOptional();
  const isPublicHome = Boolean(publicCtx?.session);
  const { currentGroup, userRole, setCurrentGroup } = useGroup();
  const { user } = useAuth();
  const { success, error } = useEnhancedToast();

  const [activePoll, setActivePoll] = useState<Poll | null>(null);
  const [activePollV2, setActivePollV2] = useState<PollV2 | null>(null);
  const [groupSettings, setGroupSettings] = useState<GroupSettings | null>(null);
  const [upcomingEvent, setUpcomingEvent] = useState<MatchEvent | null>(null);
  const [lastCompleted, setLastCompleted] = useState<CompletedMatchContext | null>(null);
  const [rsvps, setRsvps] = useState<Rsvp[]>([]);
  const [groupPlayers, setGroupPlayers] = useState<PlayerRecord[]>([]);
  const [rosterRatings, setRosterRatings] = useState<
    { id: number; name: string; skills: number; effort: number; stamina: number }[]
  >([]);
  const [guestProfiles, setGuestProfiles] = useState<GuestProfileCache>({});
  const [resultHistory, setResultHistory] = useState<MatchResultEdit[]>([]);
  const [loading, setLoading] = useState(true);
  const [creatingPoll, setCreatingPoll] = useState(false);
  const [closingPoll, setClosingPoll] = useState(false);
  const [confirmingPoll, setConfirmingPoll] = useState(false);
  const [tiebreakOpen, setTiebreakOpen] = useState(false);
  const [tiebreakOptionIds, setTiebreakOptionIds] = useState<string[]>([]);
  const [tiebreakSelectedId, setTiebreakSelectedId] = useState<string | null>(null);
  const [changingRsvpId, setChangingRsvpId] = useState<string | null>(null);
  const [currentUserPlayerId, setCurrentUserPlayerId] = useState<number | null>(null);
  const [skippedStaleResult, setSkippedStaleResult] = useState(false);
  const [pollModalOpen, setPollModalOpen] = useState(false);
  const [cycleShareDialogOpen, setCycleShareDialogOpen] = useState(false);
  const [weeklyAvailability, setWeeklyAvailability] = useState<WeeklyAvailability[]>([]);
  const [userAvailabilityDates, setUserAvailabilityDates] = useState<string[]>([]);
  const [pendingAvailabilityDates, setPendingAvailabilityDates] = useState<string[]>([]);
  const [settingUpCycleShare, setSettingUpCycleShare] = useState(false);
  const [cycleSharePasswordMemory, setCycleSharePasswordMemory] = useState("");
  const [disputing, setDisputing] = useState(false);
  const [cancelMatchOpen, setCancelMatchOpen] = useState(false);
  const [cancellingMatch, setCancellingMatch] = useState(false);
  const [publishingLineup, setPublishingLineup] = useState(false);
  const [enterResultOpen, setEnterResultOpen] = useState(false);
  const [startingPostKickoff, setStartingPostKickoff] = useState(false);
  const [weatherSummary, setWeatherSummary] = useState<string | null>(null);
  const [groupMatches, setGroupMatches] = useState<MatchRecord[]>([]);
  const [publicPollsV2Enabled, setPublicPollsV2Enabled] = useState(false);
  const [publicPhase, setPublicPhase] = useState<HomePhase>("poll_inactive");
  const [publicLinkStatus, setPublicLinkStatus] = useState<PublicCycleLinkStatus>("active");
  const [publicResultOverdue, setPublicResultOverdue] = useState(false);
  const [pollUiFormat, setPollUiFormat] = useState<ActivePollFormat>("v2");

  const [phaseOverride, setPhaseOverride] = useState<PhaseOverride>("auto");
  const [devRoleOverride, setDevRoleOverride] = useState<DevRoleOverride>("admin");
  const [mockUserVote, setMockUserVote] = useState<string | null>(null);
  const [mockRsvpStatus, setMockRsvpStatus] = useState<"going" | "maybe" | "out" | "reserve" | null>(null);
  const [mockGuests, setMockGuests] = useState<string[]>([]);
  const [mockManagedPlayerRsvps, setMockManagedPlayerRsvps] = useState<
    Record<number, RsvpStatus>
  >({});
  const [mockPublishedLineup, setMockPublishedLineup] = useState<{
    teamA: number[];
    teamB: number[];
  } | null>(null);
  const [resultScores, setResultScores] = useState({ a: "", b: "" });
  const [resultWinner, setResultWinner] = useState<"A" | "B" | "Draw" | null>(null);

  const currentUserId = user?.id ?? null;
  const showDevPanel = import.meta.env.DEV && !isPublicHome;
  const isRealAdmin = !isPublicHome && userRole === "Admin";
  const isAdmin =
    isPublicHome
      ? false
      : showDevPanel && phaseOverride !== "auto"
        ? devRoleOverride === "admin"
        : isRealAdmin;
  const isAutoPhase = isPublicHome ? true : phaseOverride === "auto";
  /** Live RSVPs only when a real convocatoria event exists; otherwise mock preview data. */
  const useLiveRsvpData = upcomingEvent != null;

  const pollsV2 = isPublicHome ? publicPollsV2Enabled : true;
  const showPollV2Ui = isPublicHome ? publicPollsV2Enabled : pollUiFormat === "v2";
  const groupId = isPublicHome
    ? publicCtx?.session?.groupId || currentGroup?.id
    : currentGroup?.id;
  const upcomingDays = useMemo(() => getUpcomingDays(AVAILABILITY_LOOKAHEAD_DAYS), []);

  const dateLocale = es;
  const relativeLabels = useMemo(
    () => ({
      today: t("v2.home.relativeDate.today"),
      tomorrow: t("v2.home.relativeDate.tomorrow"),
      thisPrefix: t("v2.home.relativeDate.thisPrefix"),
      hoursSuffix: t("v2.home.relativeDate.hoursSuffix"),
    }),
    [t]
  );

  const applyScoresFromMatch = (match: MatchRecord | null) => {
    if (match?.scorea != null && match?.scoreb != null) {
      setResultScores({ a: String(match.scorea), b: String(match.scoreb) });
    } else {
      setResultScores({ a: "", b: "" });
    }
    setResultWinner(match?.winner ?? null);
  };

  const applyPollLoad = (result: { poll: Poll | PollV2 | null; format: ActivePollFormat }) => {
    if (!isPublicHome) {
      setPollUiFormat(result.format);
    }
    if (result.format === "v2") {
      setActivePollV2(result.poll as PollV2 | null);
      setActivePoll(null);
    } else {
      setActivePoll(result.poll as Poll | null);
      setActivePollV2(null);
    }
  };

  const refreshPoll = async () => {
    if (!groupId) return;
    if (isPublicHome) {
      await loadPublicData({ silent: true });
      return;
    }
    applyPollLoad(await fetchActivePollForHome(groupId));
  };

  const refreshRsvps = async () => {
    if (!groupId) return;
    const currentEvent = await fetchCurrentMatchEvent(groupId);
    setUpcomingEvent(currentEvent);
    if (currentEvent) {
      setRsvps(await fetchRsvpsForEvent(currentEvent.id));
    } else {
      setRsvps([]);
    }
  };

  const loadPublicData = async (options?: { silent?: boolean }) => {
    const session = publicCtx?.session;
    if (!session) return;
    const silent = options?.silent ?? false;

    try {
      if (!silent) setLoading(true);
      const snapshot = await fetchPublicCycleHome({
        token: session.token,
        password: session.password,
        playerId: session.playerId,
      });

      setCurrentGroup({
        id: snapshot.group.id,
        name: snapshot.group.name,
        created_by: null,
      });

      if (session.groupId !== snapshot.group.id) {
        publicCtx?.setSession({
          ...session,
          groupId: snapshot.group.id,
          groupName: snapshot.group.name,
        });
      }

      setPublicPollsV2Enabled(snapshot.pollsV2);
      setPublicPhase(snapshot.phase);
      setPublicLinkStatus(snapshot.linkStatus);
      setPublicResultOverdue(snapshot.resultOverdue);
      setGroupSettings(snapshot.groupSettings);
      setGroupPlayers(snapshot.players);
      setUpcomingEvent(snapshot.event);
      setRsvps(snapshot.rsvps);
      setLastCompleted(snapshot.lastCompleted);
      setCurrentUserPlayerId(session.playerId);

      if (snapshot.pollsV2) {
        setActivePollV2((snapshot.poll as PollV2 | null) ?? null);
        setActivePoll(null);
      } else {
        setActivePoll((snapshot.poll as Poll | null) ?? null);
        setActivePollV2(null);
      }
      setPollUiFormat(snapshot.pollsV2 ? "v2" : "legacy");

      if (snapshot.event?.event_date) {
        const wx = await fetchWeatherForecast(
          snapshot.event.event_date,
          snapshot.event.location ?? snapshot.groupSettings?.default_location
        );
        setWeatherSummary(
          wx
            ? wx.temperature != null
              ? `${wx.summary}, ${wx.temperature}°C`
              : wx.summary
            : null
        );
      } else {
        setWeatherSummary(null);
      }

      applyScoresFromMatch(snapshot.lastCompleted?.match ?? null);
      setResultHistory([]);
      setWeeklyAvailability([]);
      setUserAvailabilityDates([]);
    } catch (err) {
      console.error("Error loading public home data:", err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadHomeDeferredData = async (
    deferredGroupId: string,
    currentEvent: MatchEvent | null,
    settings: GroupSettings | null,
    hasCurrentEvent: boolean
  ) => {
    try {
      const availabilityDays = getUpcomingDays(AVAILABILITY_LOOKAHEAD_DAYS);

      const [
        weatherResult,
        completed,
        matchRowsResult,
        rosterResult,
        availabilityResult,
      ] = await Promise.all([
        currentEvent?.event_date
          ? fetchWeatherForecast(
              currentEvent.event_date,
              currentEvent.location ?? settings?.default_location
            )
          : Promise.resolve(null),
        fetchLastCompletedMatch(deferredGroupId),
        supabase
          .from("matches")
          .select("*")
          .eq("group_id", deferredGroupId)
          .order("match_date", { ascending: true }),
        Promise.all([
          supabase
            .from("players")
            .select("id, name, skills, effort, stamina")
            .eq("group_id", deferredGroupId),
          fetchGuestProfiles(deferredGroupId),
        ]),
        Promise.all([
          fetchWeeklyAvailabilityForDays(deferredGroupId, availabilityDays),
          currentUserId
            ? fetchUserAvailabilityDatesForDays(
                deferredGroupId,
                currentUserId,
                availabilityDays
              )
            : Promise.resolve([] as string[]),
        ]),
      ]);

      if (!currentEvent?.event_date) {
        setWeatherSummary(null);
      } else {
        setWeatherSummary(
          weatherResult
            ? weatherResult.temperature != null
              ? `${weatherResult.summary}, ${weatherResult.temperature}°C`
              : weatherResult.summary
            : null
        );
      }

      setLastCompleted(completed);
      setGroupMatches((matchRowsResult.data ?? []) as MatchRecord[]);

      const [{ data: rosterRows }, guestCache] = rosterResult;
      setRosterRatings((rosterRows ?? []) as typeof rosterRatings);
      setGuestProfiles(guestCache);

      const [availabilityRows, userDates] = availabilityResult;
      setWeeklyAvailability(availabilityRows);
      setUserAvailabilityDates(userDates);

      if (!hasCurrentEvent) {
        applyScoresFromMatch(completed?.match ?? null);
      }

      const historyMatchId = hasCurrentEvent
        ? currentEvent?.match_id ?? null
        : completed?.match?.id ?? null;
      setResultHistory(
        historyMatchId != null
          ? await fetchMatchResultHistory(historyMatchId, deferredGroupId)
          : []
      );
    } catch (err) {
      console.error("Error loading deferred home data:", err);
    }
  };

  const loadData = async (options?: { silent?: boolean }) => {
    if (isPublicHome) {
      await loadPublicData(options);
      return;
    }
    if (!groupId) return;
    const silent = options?.silent ?? false;
    let loadedEvent: MatchEvent | null = null;
    let loadedSettings: GroupSettings | null = null;
    try {
      if (!silent) setLoading(true);

      const [pollLoad, settings, players, currentEvent] = await Promise.all([
        fetchActivePollForHome(groupId),
        fetchGroupSettings(groupId),
        fetchGroupPlayers(groupId),
        fetchCurrentMatchEvent(groupId),
      ]);

      loadedEvent = currentEvent;
      loadedSettings = settings;

      applyPollLoad(pollLoad);
      setGroupSettings(settings);
      setGroupPlayers(players);
      setUpcomingEvent(currentEvent);

      const [eventRsvps, playerId, linkedMatch] = await Promise.all([
        currentEvent
          ? fetchRsvpsForEvent(currentEvent.id)
          : Promise.resolve([] as Rsvp[]),
        currentUserId
          ? ensureCurrentUserPlayerId(
              groupId,
              currentUserId,
              (user?.user_metadata?.full_name as string | undefined) ?? undefined
            )
          : Promise.resolve(null as number | null),
        currentEvent?.match_id != null
          ? fetchMatchById(currentEvent.match_id)
          : Promise.resolve(null),
      ]);

      setRsvps(currentEvent ? eventRsvps : []);
      setCurrentUserPlayerId(playerId);

      if (currentEvent) {
        applyScoresFromMatch(linkedMatch);
      } else {
        applyScoresFromMatch(null);
        setResultHistory([]);
      }
    } catch (err) {
      console.error("Error loading home data:", err);
    } finally {
      if (!silent) setLoading(false);
    }

    void loadHomeDeferredData(
      groupId,
      loadedEvent,
      loadedSettings,
      Boolean(loadedEvent)
    );
  };

  useEffect(() => {
    if (isPublicHome) {
      if (publicCtx?.session) {
        loadData();
      }
    } else if (groupId) {
      loadData();
    }
  }, [groupId, currentUserId, user?.user_metadata?.full_name, isPublicHome, publicCtx?.session?.playerId, publicCtx?.session?.token]);

  const isCompletedStale =
    lastCompleted != null &&
    Date.now() - parseISO(lastCompleted.event.event_date).getTime() >= COMPLETED_RESET_MS;

  const activePhase = isPublicHome
    ? publicPhase
    : deriveHomePhase({
        phaseOverride,
        activePoll: showPollV2Ui ? activePollV2 : activePoll,
        upcomingEvent,
        lastCompleted,
      });

  const isRsvpPhase = activePhase === "convocatoria";
  const canManageRsvp = !isPublicHome && isAdmin && isRsvpPhase;
  const canAddGuest = !isPublicHome && isRsvpPhase && isAutoPhase;
  const showCycleShare =
    !isPublicHome && ["poll_open", "convocatoria"].includes(activePhase);
  const activePollForShare = showPollV2Ui ? activePollV2 : activePoll;
  const cycleShare = useMemo(
    () => deriveCycleShareState(activePollForShare, upcomingEvent),
    [activePollForShare, upcomingEvent]
  );

  const maxPlayers = upcomingEvent?.max_players ?? 10;
  const confirmation = getConfirmationProgress(
    rsvps,
    maxPlayers,
    groupSettings?.min_confirm_pct ?? 70
  );
  const displayMinConfirmPct = groupSettings?.min_confirm_pct ?? 70;

  // §5 edge case: a previously confirmed event can drop below the minimum quota
  // (e.g. someone marks "out" with no reserves). When kickoff is ≤24h away and we
  // are no longer at/above min_players, surface an at-risk alert on Home so the
  // group can scramble for more players before it gets cancelled.
  const HOURS_24_MS = 24 * 60 * 60 * 1000;
  const msUntilKickoff = upcomingEvent
    ? parseISO(upcomingEvent.event_date).getTime() - Date.now()
    : null;
  const isWithin24hBeforeKickoff =
    msUntilKickoff != null && msUntilKickoff > 0 && msUntilKickoff <= HOURS_24_MS;
  const showDropBelowMinAlert =
    isAutoPhase &&
    upcomingEvent != null &&
    isRsvpPhase &&
    isWithin24hBeforeKickoff &&
    !confirmation.confirmed;
  const playersNeeded = Math.max(0, confirmation.minPlayers - confirmation.goingCount);

  const playerNameById = useMemo(() => {
    return new Map(groupPlayers.map((player) => [player.id, player.name]));
  }, [groupPlayers]);

  const rosterById = useMemo(
    () => new Map(rosterRatings.map((player) => [player.id, player])),
    [rosterRatings]
  );

  const guestNameByRsvpId = useMemo(
    () =>
      new Map(
        rsvps
          .filter((rsvp) => rsvp.guest_name)
          .map((rsvp) => [rsvp.id, rsvp.guest_name as string])
      ),
    [rsvps]
  );

  const groupAvgRating = useMemo(() => groupAverageRating(rosterRatings), [rosterRatings]);

  const formatRsvpName = (rsvp: Rsvp) =>
    rsvp.guest_name ?? playerNameById.get(rsvp.player_id ?? -1) ?? `Player ${rsvp.player_id}`;

  const toRosterEntry = (rsvp: Rsvp): RosterEntry => ({
    id: rsvp.id,
    name: formatRsvpName(rsvp),
    playerId: rsvp.player_id,
    guestName: rsvp.guest_name,
    status: rsvp.status,
  });

  const toMockRosterEntries = (names: string[], status: RsvpStatus): RosterEntry[] =>
    names.map((name, i) => ({
      id: `mock-${status}-${i}`,
      name,
      status,
    }));

  const mockRosterEntries = useMemo(() => {
    const managedByStatus = (status: RsvpStatus): RosterEntry[] =>
      Object.entries(mockManagedPlayerRsvps)
        .filter(([, entryStatus]) => entryStatus === status)
        .map(([playerIdStr]) => {
          const playerId = Number(playerIdStr);
          return {
            id: `mock-managed-${playerId}`,
            name: playerNameById.get(playerId) ?? `Player ${playerId}`,
            playerId,
            status,
          };
        });

    const base = {
      going: [
        ...toMockRosterEntries([...MOCK_ROSTER.going, ...mockGuests], "going"),
        ...managedByStatus("going"),
      ],
      maybe: [
        ...toMockRosterEntries(MOCK_ROSTER.maybe, "maybe"),
        ...managedByStatus("maybe"),
      ],
      out: [
        ...toMockRosterEntries(MOCK_ROSTER.out, "out"),
        ...managedByStatus("out"),
      ],
      reserve: [
        ...toMockRosterEntries(MOCK_ROSTER.reserve, "reserve"),
        ...managedByStatus("reserve"),
      ],
    };

    if (useLiveRsvpData || !mockRsvpStatus) {
      return base;
    }

    const selfName =
      (currentUserPlayerId != null
        ? playerNameById.get(currentUserPlayerId)
        : undefined) ??
      (user?.user_metadata?.full_name as string | undefined) ??
      "Yo";
    const selfEntry: RosterEntry = {
      id: "mock-self",
      name: selfName,
      playerId: currentUserPlayerId,
      status: mockRsvpStatus,
    };
    const stripSelf = (entries: RosterEntry[]) =>
      entries.filter(
        (entry) =>
          entry.id !== selfEntry.id &&
          (currentUserPlayerId == null || entry.playerId !== currentUserPlayerId)
      );

    return {
      going:
        mockRsvpStatus === "going"
          ? [...stripSelf(base.going), selfEntry]
          : stripSelf(base.going),
      maybe:
        mockRsvpStatus === "maybe"
          ? [...stripSelf(base.maybe), selfEntry]
          : stripSelf(base.maybe),
      out:
        mockRsvpStatus === "out"
          ? [...stripSelf(base.out), selfEntry]
          : stripSelf(base.out),
      reserve:
        mockRsvpStatus === "reserve"
          ? [...stripSelf(base.reserve), selfEntry]
          : stripSelf(base.reserve),
    };
  }, [
    mockGuests,
    mockManagedPlayerRsvps,
    mockRsvpStatus,
    playerNameById,
    currentUserPlayerId,
    useLiveRsvpData,
    user?.user_metadata?.full_name,
  ]);

  const mockGoingCount = mockRosterEntries.going.length;

  const mockGoingPlayersForLineup = useMemo((): LineupPlayer[] => {
    const base: LineupPlayer[] = [...MOCK_ROSTER.going, ...mockGuests].map((name, i) => ({
      key: `player-${9000 + i}`,
      name,
      playerId: 9000 + i,
      balanceScore: 75,
    }));
    const managed = Object.entries(mockManagedPlayerRsvps)
      .filter(([, status]) => status === "going")
      .flatMap(([playerIdStr]) => {
        const playerId = Number(playerIdStr);
        const roster = rosterById.get(playerId);
        if (!roster) return [];
        return [
          {
            key: `player-${playerId}`,
            name: roster.name,
            playerId,
            balanceScore: playerBalanceScore(roster),
          },
        ];
      });
    const selfGoing =
      !useLiveRsvpData && mockRsvpStatus === "going"
        ? [
            {
              key: currentUserPlayerId != null ? `player-${currentUserPlayerId}` : "player-self",
              name:
                (currentUserPlayerId != null
                  ? playerNameById.get(currentUserPlayerId)
                  : undefined) ??
                (user?.user_metadata?.full_name as string | undefined) ??
                "Yo",
              playerId: currentUserPlayerId ?? undefined,
              balanceScore: currentUserPlayerId
                ? playerBalanceScore(rosterById.get(currentUserPlayerId) ?? { id: currentUserPlayerId, name: "", skills: 75, effort: 75, stamina: 75 })
                : 75,
            },
          ]
        : [];
    return [...base, ...managed, ...selfGoing];
  }, [
    mockGuests,
    mockManagedPlayerRsvps,
    mockRsvpStatus,
    mockRosterEntries.going.length,
    rosterById,
    groupAvgRating,
    useLiveRsvpData,
    currentUserPlayerId,
    playerNameById,
    user?.user_metadata?.full_name,
  ]);

  const unrespondedPlayers = useMemo(
    () =>
      groupPlayers.filter(
        (player) =>
          !rsvps.some((rsvp) => rsvp.player_id === player.id) &&
          mockManagedPlayerRsvps[player.id] == null
      ),
    [groupPlayers, rsvps, mockManagedPlayerRsvps]
  );

  const goingRsvps = rsvps.filter((r) => r.status === "going");
  const recapEvent = lastCompleted?.event ?? upcomingEvent;
  const recapMatch = lastCompleted?.match ?? null;
  const resultEvent = upcomingEvent ?? lastCompleted?.event ?? null;
  const hasExistingResult = Boolean(resultEvent?.match_id);
  const goingPlayerIds = goingRsvps.map((r) => r.player_id).filter((id): id is number => id != null);
  const maybeRsvps = rsvps.filter((r) => r.status === "maybe");
  const outRsvps = rsvps.filter((r) => r.status === "out");
  const reserveRsvps = rsvps.filter((r) => r.status === "reserve");
  const currentUserRsvpStatus =
    currentUserPlayerId != null
      ? rsvps.find((rsvp) => rsvp.player_id === currentUserPlayerId)?.status ?? null
      : null;

  const userVoteOptionId = useMemo(() => {
    if (isPublicHome && currentUserPlayerId != null && activePoll?.options) {
      for (const opt of activePoll.options) {
        const votes = (opt as PollOption).votes ?? [];
        if (votes.some((vote) => vote.player_id === currentUserPlayerId)) {
          return opt.id;
        }
      }
      return null;
    }
    return getUserPollVoteOptionId(activePoll?.options, currentUserId);
  }, [activePoll?.options, currentUserId, currentUserPlayerId, isPublicHome]);

  const useMockPollOptions = !isAutoPhase && !activePoll;

  // --- Derived view models -------------------------------------------------

  const eventLocation =
    upcomingEvent?.location ?? groupSettings?.default_location ?? null;
  const eventRelativeDate = upcomingEvent
    ? relativeEventDate(upcomingEvent.event_date, {
        locale: dateLocale,
        labels: relativeLabels,
      })
    : "";

  const pollOptionsVM: PollOptionVM[] = (
    useMockPollOptions ? MOCK_OPTIONS : activePoll?.options ?? []
  ).map((opt) => {
    const voteCount = useMockPollOptions
      ? (opt as (typeof MOCK_OPTIONS)[number]).votes
      : (opt as PollOption).votes?.length ?? 0;
    const isSelected = useMockPollOptions
      ? mockUserVote === opt.id
      : userVoteOptionId === opt.id;
    const label =
      opt.label || format(parseISO(opt.proposed_date), "EEEE d MMM, HH:mm", {
        locale: dateLocale,
      });
    return { id: opt.id, label, voteCount, isSelected };
  });

  // Confirmed phase: detect a published lineup from the JSONB; never derive UI
  // teams from going RSVPs — only the admin-published lineup is shown.
  const publishedLineup = isAutoPhase && upcomingEvent ? hasPublishedLineup(upcomingEvent) : false;
  const collectingHasPublishedLineup =
    isAutoPhase ? publishedLineup : mockPublishedLineup != null;

  let confirmedTeamA: TeamPlayerVM[] = [];
  let confirmedTeamB: TeamPlayerVM[] = [];
  let currentUserTeam: "A" | "B" | null = null;
  if (isAutoPhase) {
    const parsed = parseEventLineup(upcomingEvent?.lineup, {
      playerNameById,
      guestNameByRsvpId,
      defaultBalanceScore: groupAvgRating,
    });
    confirmedTeamA = lineupMembersToTeamPlayerVM(parsed.teamA, "a", currentUserPlayerId);
    confirmedTeamB = lineupMembersToTeamPlayerVM(parsed.teamB, "b", currentUserPlayerId);
    if (currentUserPlayerId != null) {
      if (parsed.teamA.some((m) => m.playerId === currentUserPlayerId)) currentUserTeam = "A";
      else if (parsed.teamB.some((m) => m.playerId === currentUserPlayerId)) currentUserTeam = "B";
    }
  } else if (isRsvpPhase && mockPublishedLineup) {
    const nameById = new Map(mockGoingPlayersForLineup.map((p) => [p.playerId ?? 0, p.name]));
    confirmedTeamA = mockPublishedLineup.teamA.map((id) => ({
      key: `a-${id}`,
      name: nameById.get(id) ?? `Player ${id}`,
      isCurrentUser: false,
    }));
    confirmedTeamB = mockPublishedLineup.teamB.map((id) => ({
      key: `b-${id}`,
      name: nameById.get(id) ?? `Player ${id}`,
      isCurrentUser: false,
    }));
  } else if (!isRsvpPhase) {
    confirmedTeamA = MOCK_LINEUP.teamA.map((name, i) => ({
      key: `a-${i}`,
      name,
      isCurrentUser: false,
    }));
    confirmedTeamB = MOCK_LINEUP.teamB.map((name, i) => ({
      key: `b-${i}`,
      name,
      isCurrentUser: false,
    }));
  }

  // Post-kickoff 48h fallback (client-only; never mutates Supabase on skip).
  const hoursSinceKickoff = upcomingEvent
    ? (Date.now() - eventKickoffTime(upcomingEvent).getTime()) / (1000 * 60 * 60)
    : null;
  const showStaleResultBanner =
    isAutoPhase &&
    hoursSinceKickoff != null &&
    hoursSinceKickoff >= 48 &&
    !hasExistingResult;

  // Completed phase derived score + winner.
  const completedScoreA = isAutoPhase
    ? recapMatch?.scorea ?? (resultScores.a !== "" ? Number(resultScores.a) : null)
    : MOCK_RECAP.scoreA;
  const completedScoreB = isAutoPhase
    ? recapMatch?.scoreb ?? (resultScores.b !== "" ? Number(resultScores.b) : null)
    : MOCK_RECAP.scoreB;
  const completedWinner: "A" | "B" | "Draw" | null =
    recapMatch?.winner ??
    (completedScoreA != null && completedScoreB != null
      ? completedScoreA > completedScoreB
        ? "A"
        : completedScoreB > completedScoreA
          ? "B"
          : "Draw"
      : null);

  const recapDateLabel = recapEvent
    ? format(parseISO(recapEvent.event_date), "EEEE d MMM", { locale: dateLocale })
    : recapMatch
      ? format(parseISO(recapMatch.match_date), "EEEE d MMM", { locale: dateLocale })
      : "";

  // Leaderboard teaser from real match data when available.
  let teaserLines: string[] = [];
  if (isAutoPhase && recapMatch && groupPlayers.length > 0) {
    teaserLines = computePostMatchHighlights(groupPlayers, groupMatches, recapMatch);
  }
  if (teaserLines.length === 0 && !isAutoPhase) {
    teaserLines = [t("v2.home.teaserStaticForm"), t("v2.home.teaserStaticChemistry")];
  }

  const availabilitySummary = useMemo(
    () =>
      aggregateAvailabilityByDay(weeklyAvailability, upcomingDays),
    [weeklyAvailability, upcomingDays]
  );

  const availabilityMap = useMemo(
    () => Object.fromEntries(availabilitySummary.map((d) => [d.date, d.count])),
    [availabilitySummary]
  );

  const showLoopReset =
    activePhase === "completed" &&
    !upcomingEvent &&
    !(showPollV2Ui ? activePollV2 : activePoll) &&
    !isCompletedStale;

  const formatOptionLabel = (opt: { label?: string | null; proposed_date: string }) =>
    opt.label || format(parseISO(opt.proposed_date), "EEEE d MMM, HH:mm", { locale: dateLocale });

  const formatScore = (scoreA: number | null, scoreB: number | null) =>
    `${scoreA ?? "—"} - ${scoreB ?? "—"}`;

  const renderResultHistory = () => {
    if (!isAutoPhase || resultHistory.length === 0) return null;

    return (
      <div className="rounded-xl border bg-accent/30 p-4 space-y-3">
        <p className="text-sm font-bold flex items-center gap-2">
          <FaHistory className="text-soccer-primary" />
          {t("v2.matches.resultHistory")}
        </p>
        <ul className="space-y-2">
          {resultHistory.slice(0, 3).map((edit) => (
            <li
              key={edit.id}
              className="flex items-center justify-between gap-3 text-sm"
            >
              <span className="font-semibold">
                {formatScore(edit.previous_scorea, edit.previous_scoreb)} →{" "}
                {formatScore(edit.new_scorea, edit.new_scoreb)}
              </span>
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {format(parseISO(edit.created_at), "d MMM · HH:mm", { locale: dateLocale })}
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const commentsEventId =
    activePhase === "completed"
      ? lastCompleted?.event?.id
      : activePhase === "post_kickoff"
        ? upcomingEvent?.id
        : null;

  const renderEventComments = () => {
    if (isPublicHome || !commentsEventId || !currentGroup?.id) return null;
    return <HomeEventComments eventId={commentsEventId} groupId={currentGroup.id} />;
  };

  const tiebreakOptions = useMemo(() => {
    const source = useMockPollOptions ? MOCK_OPTIONS : activePoll?.options ?? [];
    return tiebreakOptionIds
      .map((id) => source.find((opt) => opt.id === id))
      .filter((opt): opt is NonNullable<typeof opt> => Boolean(opt));
  }, [tiebreakOptionIds, useMockPollOptions, activePoll?.options]);

  const handleCreatePollV2 = async (input: CreatePollV2Input) => {
    if (!groupId) return null;
    try {
      setCreatingPoll(true);
      const poll = await createPollV2(groupId, input);
      success({
        title: t("v2.home.createPollSuccess"),
        description: t("v2.home.createPollSuccessDesc"),
      });
      setSkippedStaleResult(false);
      await loadData({ silent: true });

      const publicPassword = input.publicPassword?.trim();
      if (publicPassword && poll.poll_public_token) {
        setCycleSharePasswordMemory(publicPassword);
        return {
          shareUrl: formatPublicCycleUrl(poll.poll_public_token),
          sharePassword: publicPassword,
        };
      }
      return null;
    } catch (e) {
      let description = t("v2.home.createPollError");
      if (e instanceof OpenPollExistsError) {
        description = t("v2.home.createPollOpenExists");
      } else if (e instanceof Error && e.message === "INVALID_DAY_COUNT") {
        description = t("v2.home.pollInvalidDayCount");
      } else if (e instanceof Error && e.message === "INVALID_TIMES_PER_DAY") {
        description = t("v2.home.pollInvalidTimes");
      }
      error({ title: t("errors.error"), description });
      return null;
    } finally {
      setCreatingPoll(false);
    }
  };

  const mapPollActionError = (err: unknown): string => {
    if (err instanceof Error) {
      if (err.message === "ADMIN_REQUIRED") return t("v2.home.adminOnly");
      return err.message;
    }
    return t("rsvp.saveError");
  };

  const handleVoteSlots = async (
    slotIds: string[],
    options?: { onSettled?: () => void }
  ) => {
    if (!activePollV2) {
      options?.onSettled?.();
      return;
    }

    try {
      if (isPublicHome && publicCtx?.session) {
        const updated = await submitPublicCyclePollVotes({
          token: publicCtx.session.token,
          password: publicCtx.session.password,
          playerId: publicCtx.session.playerId,
          slotIds,
        });
        if (updated) {
          if (publicPollsV2Enabled) setActivePollV2(updated as PollV2);
          else setActivePoll(updated as Poll);
        } else {
          await loadData({ silent: true });
        }
      } else if (!isAutoPhase) {
        return;
      } else {
        if (!currentUserId) throw new Error("Not logged in");
        await votePollSlots(activePollV2.id, slotIds);
        await refreshPoll();
      }
    } catch (err) {
      error({
        title: t("errors.error"),
        description: mapPublicCycleError(err, t("rsvp.saveError")),
      });
      if (isPublicHome) {
        await loadData({ silent: true });
      } else {
        await refreshPoll();
      }
    } finally {
      options?.onSettled?.();
    }
  };

  const handleConfirmPollSlot = async (slotId: string) => {
    if (!activePollV2) return;
    try {
      setConfirmingPoll(true);
      await confirmPollSlot(activePollV2.id, slotId);
      success({
        title: t("v2.home.pollClosed"),
        description: t("v2.home.pollClosedDesc"),
      });
      await loadData({ silent: true });
    } catch (err) {
      error({
        title: t("errors.error"),
        description: mapPollActionError(err),
      });
    } finally {
      setConfirmingPoll(false);
    }
  };

  const handleCancelPoll = async () => {
    if (!activePollV2) return;
    try {
      setClosingPoll(true);
      await cancelPoll(activePollV2.id);
      setSkippedStaleResult(false);
      success({
        title: t("v2.home.pollCancelled"),
        description: t("v2.home.pollCancelledDesc"),
      });
      await loadData({ silent: true });
    } catch {
      error({ title: t("errors.error"), description: t("rsvp.saveError") });
    } finally {
      setClosingPoll(false);
    }
  };

  const handleConfirmCancelMatch = async () => {
    if (!upcomingEvent) return;
    try {
      setCancellingMatch(true);
      await cancelMatchEvent(upcomingEvent.id);
      success({
        title: t("v2.home.matchCancelled"),
        description: t("v2.home.matchCancelledDesc"),
      });
      setCancelMatchOpen(false);
      setSkippedStaleResult(false);
      await loadData({ silent: true });
    } catch {
      error({ title: t("errors.error"), description: t("rsvp.saveError") });
    } finally {
      setCancellingMatch(false);
    }
  };

  const handleCycleShareSetupPassword = async (password: string): Promise<string | null> => {
    const trimmed = password.trim();
    if (trimmed.length < 4) return null;

    const pollId = showPollV2Ui ? activePollV2?.id : activePoll?.id;
    try {
      setSettingUpCycleShare(true);
      let url: string | null = null;
      if (pollId) {
        const result = await setupPublicPollPassword(pollId, trimmed);
        url = result.url;
        success({
          title: t("v2.home.publicLinkReady"),
          description: t("v2.home.publicPollLinkReadyDesc"),
        });
      } else if (upcomingEvent) {
        const result = await setupPublicRsvpPassword(upcomingEvent.id, trimmed);
        url = result.url;
        success({
          title: t("v2.home.publicLinkReady"),
          description: t("v2.home.cycleShareReadyDesc"),
        });
      } else {
        return null;
      }
      setCycleSharePasswordMemory(trimmed);
      await loadData({ silent: true });
      return url;
    } catch {
      error({ title: t("errors.error"), description: t("v2.home.publicLinkError") });
      return null;
    } finally {
      setSettingUpCycleShare(false);
    }
  };

  const handleToggleAvailability = async (date: string) => {
    if (!groupId || !currentUserId) return;
    if (pendingAvailabilityDates.includes(date)) return;

    const wasSelected = userAvailabilityDates.includes(date);
    setUserAvailabilityDates((prev) =>
      wasSelected ? prev.filter((d) => d !== date) : [...prev, date]
    );
    setPendingAvailabilityDates((prev) => [...prev, date]);

    try {
      await toggleWeeklyAvailability(
        groupId,
        date,
        weekStartOf(parseISO(`${date}T12:00:00`)),
        currentUserId,
        currentUserPlayerId
      );
      const rows = await fetchWeeklyAvailabilityForDays(groupId, getUpcomingDays(AVAILABILITY_LOOKAHEAD_DAYS));
      setWeeklyAvailability(rows);
    } catch {
      setUserAvailabilityDates((prev) =>
        wasSelected ? [...prev, date] : prev.filter((d) => d !== date)
      );
      error({ title: t("errors.error"), description: t("v2.home.availabilityError") });
    } finally {
      setPendingAvailabilityDates((prev) => prev.filter((d) => d !== date));
    }
  };

  const handleVote = async (optionId: string) => {
    if (!isAutoPhase || !activePoll) {
      setMockUserVote(optionId);
      success({
        title: t("v2.home.voteRecorded"),
        description: t("v2.home.voteMock"),
      });
      return;
    }

    if (isPublicHome && publicCtx?.session) {
      try {
        const updated = await submitPublicCyclePollVotes({
          token: publicCtx.session.token,
          password: publicCtx.session.password,
          playerId: publicCtx.session.playerId,
          slotIds: [optionId],
        });
        if (updated) {
          if (publicPollsV2Enabled) setActivePollV2(updated as PollV2);
          else setActivePoll(updated as Poll);
        } else {
          await loadData({ silent: true });
        }
        success({
          title: t("v2.home.voteRecorded"),
          description: t("v2.home.voteCounted"),
        });
      } catch (err) {
        error({
          title: t("errors.error"),
          description: mapPublicCycleError(err, t("rsvp.saveError")),
        });
      }
      return;
    }

    try {
      if (!currentUserId) throw new Error("Not logged in");
      await voteInPoll(activePoll.id, optionId, { userId: currentUserId });
      await refreshPoll();
    } catch {
      error({ title: t("errors.error"), description: t("rsvp.saveError") });
    }
  };

  const finalizePoll = async (winningOptionId: string) => {
    if (!activePoll) return;
    try {
      setClosingPoll(true);
      await closePollAndCreateEvent(activePoll.id, winningOptionId, {});
      success({
        title: t("v2.home.pollClosed"),
        description: t("v2.home.pollClosedDesc"),
      });
      await loadData({ silent: true });
    } catch (err) {
      error({
        title: t("errors.error"),
        description: mapPollActionError(err),
      });
    } finally {
      setClosingPoll(false);
    }
  };

  const handleClosePoll = async () => {
    if (!isAutoPhase || !activePoll) {
      if (useMockPollOptions) {
        const mockOptions = MOCK_OPTIONS.map((o) => ({ id: o.id, votes: o.votes }));
        const { winner, isTie, tiedOptions } = getPollWinningOption(mockOptions);
        if (isTie) {
          setTiebreakOptionIds(tiedOptions.map((o) => o.id));
          setTiebreakSelectedId(tiedOptions[0]?.id ?? null);
          setTiebreakOpen(true);
          return;
        }
        if (!winner) {
          error({
            title: t("v2.home.pollNoVotes"),
            description: t("v2.home.pollNoVotesDesc"),
          });
          return;
        }
      }
      setPhaseOverride("convocatoria");
      success({
        title: t("v2.home.pollClosed"),
        description: t("v2.home.pollClosedMock"),
      });
      return;
    }

    if (!activePoll.options?.length) {
      error({
        title: t("errors.error"),
        description: t("v2.home.pollNoVotes"),
      });
      return;
    }

    const options = activePoll.options.map((opt) => ({
      id: opt.id,
      votes: opt.votes?.length ?? 0,
    }));

    const { winner, isTie, tiedOptions } = getPollWinningOption(options);

    if (isTie) {
      // §3: ties are broken manually. Surface a picker so a member can choose
      // the winning option, which then flows into closePollAndCreateEvent.
      setTiebreakOptionIds(tiedOptions.map((o) => o.id));
      setTiebreakSelectedId(tiedOptions[0]?.id ?? null);
      setTiebreakOpen(true);
      return;
    }

    if (!winner) {
      error({
        title: t("v2.home.pollNoVotes"),
        description: t("v2.home.pollNoVotesDesc"),
      });
      return;
    }

    await finalizePoll(winner.id);
  };

  const handleConfirmTiebreak = async () => {
    if (!tiebreakSelectedId) return;
    setTiebreakOpen(false);

    if (!isAutoPhase || !activePoll) {
      setPhaseOverride("convocatoria");
      success({
        title: t("v2.home.pollClosed"),
        description: t("v2.home.pollClosedMock"),
      });
      return;
    }

    await finalizePoll(tiebreakSelectedId);
  };

  const mapRsvpError = (err: unknown): string => {
    if (err instanceof Error) {
      if (err.message === "EVENT_FULL") return t("v2.home.eventFull");
      if (err.message === "RESERVES_FULL") return t("v2.home.reservesFull");
      return err.message;
    }
    return t("rsvp.saveError");
  };

  const rsvpStatusLabel = (status: RsvpStatus) =>
    status === "reserve" ? t("v2.home.reserveStatus") : t(`v2.home.${status}`);

  const handleRsvp = async (status: "going" | "maybe" | "out" | "reserve") => {
    const activeStatus = useLiveRsvpData ? currentUserRsvpStatus : mockRsvpStatus;
    const isToggleOff = status !== "reserve" && activeStatus === status;

    if (!useLiveRsvpData) {
      if (isToggleOff) {
        setMockRsvpStatus(null);
        success({
          title: t("v2.home.rsvpCleared"),
          description: t("v2.home.rsvpClearedDesc"),
        });
        return;
      }
      setMockRsvpStatus(status);
      success({
        title: t("v2.home.rsvpSaved"),
        description: t("v2.home.rsvpUpdatedMock", { status }),
      });
      return;
    }

    if (isPublicHome && publicCtx?.session) {
      if (isToggleOff) {
        try {
          await submitPublicCycleRsvp({
            token: publicCtx.session.token,
            password: publicCtx.session.password,
            playerId: publicCtx.session.playerId,
            clear: true,
          });
          success({
            title: t("v2.home.rsvpCleared"),
            description: t("v2.home.rsvpClearedDesc"),
          });
          await loadData({ silent: true });
        } catch (err) {
          error({
            title: t("errors.error"),
            description: mapPublicCycleError(err, mapRsvpError(err)),
          });
        }
        return;
      }

      const publicStatus = status === "reserve" ? "going" : status;
      try {
        const appliedStatus = await submitPublicCycleRsvp({
          token: publicCtx.session.token,
          password: publicCtx.session.password,
          playerId: publicCtx.session.playerId,
          status: publicStatus,
        });
        success({
          title: t("v2.home.rsvpSaved"),
          description: t("v2.home.rsvpSavedDesc", { status: appliedStatus }),
        });
        await loadData({ silent: true });
      } catch (err) {
        error({
          title: t("errors.error"),
          description: mapPublicCycleError(err, mapRsvpError(err)),
        });
      }
      return;
    }

    let previousRsvps = rsvps;
    try {
      if (!currentUserId) throw new Error("Not logged in");
      if (!groupId) throw new Error("No group selected");
      let playerId = currentUserPlayerId;
      if (!playerId) {
        playerId = await ensureCurrentUserPlayerId(
          groupId,
          currentUserId,
          (user?.user_metadata?.full_name as string | undefined) ?? undefined
        );
        setCurrentUserPlayerId(playerId);
      }
      if (!playerId) {
        throw new Error(t("v2.home.playerNotLinked"));
      }

      previousRsvps = rsvps;

      if (isToggleOff) {
        setRsvps((prev) => prev.filter((r) => r.player_id !== playerId));
        await clearRsvp(
          upcomingEvent,
          { playerId },
          {
            maxReserves: groupSettings?.max_reserves,
            minConfirmPct: groupSettings?.min_confirm_pct,
          }
        );
        success({
          title: t("v2.home.rsvpCleared"),
          description: t("v2.home.rsvpClearedDesc"),
        });
        await refreshRsvps();
        return;
      }

      setRsvps((prev) => {
        const idx = prev.findIndex((r) => r.player_id === playerId);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], status };
          return next;
        }
        return [
          ...prev,
          {
            id: `optimistic-${playerId}`,
            event_id: upcomingEvent.id,
            player_id: playerId,
            guest_name: null,
            status,
            token: "",
          },
        ];
      });

      const { appliedStatus } = await applyRsvpChange(
        upcomingEvent,
        status,
        { playerId, addedByUserId: currentUserId },
        {
          maxReserves: groupSettings?.max_reserves,
          minConfirmPct: groupSettings?.min_confirm_pct,
        }
      );

      setRsvps((prev) => {
        const idx = prev.findIndex((r) => r.player_id === playerId);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], status: appliedStatus };
          return next;
        }
        return prev;
      });

      if (status === "going" && appliedStatus === "reserve") {
        success({
          title: t("v2.home.rsvpSaved"),
          description: t("v2.home.addedAsReserve"),
        });
      } else {
        success({
          title: t("v2.home.rsvpSaved"),
          description: t("v2.home.rsvpSavedDesc", { status: appliedStatus }),
        });
      }
      await refreshRsvps();
    } catch (err: unknown) {
      setRsvps(previousRsvps);
      error({ title: t("errors.error"), description: mapRsvpError(err) });
    }
  };

  const handleAdminRsvpChange = async (entry: RosterEntry, status: RsvpStatus) => {
    if (!canManageRsvp) return;
    if (entry.status === status) return;

    if (!useLiveRsvpData) {
      if (entry.playerId != null) {
        setMockManagedPlayerRsvps((prev) => ({ ...prev, [entry.playerId!]: status }));
      }
      success({
        title: t("v2.home.rsvpSaved"),
        description: t("v2.home.adminRsvpUpdated", {
          name: entry.name,
          status: rsvpStatusLabel(status),
        }),
      });
      return;
    }

    const pendingId = entry.id;
    const previousRsvps = rsvps;

    try {
      setChangingRsvpId(pendingId);
      setRsvps((prev) =>
        prev.map((rsvp) => (rsvp.id === entry.id ? { ...rsvp, status } : rsvp))
      );

      const actor =
        entry.playerId != null
          ? { playerId: entry.playerId, addedByUserId: currentUserId ?? undefined }
          : { guestName: entry.guestName ?? undefined, addedByUserId: currentUserId ?? undefined };

      const { appliedStatus, rsvps: updatedRsvps } = await applyRsvpChange(
        upcomingEvent,
        status,
        actor,
        {
          maxReserves: groupSettings?.max_reserves,
          minConfirmPct: groupSettings?.min_confirm_pct,
        }
      );

      success({
        title: t("v2.home.rsvpSaved"),
        description: t("v2.home.adminRsvpUpdated", {
          name: entry.name,
          status: rsvpStatusLabel(appliedStatus),
        }),
      });
      setRsvps(updatedRsvps);
    } catch (err: unknown) {
      setRsvps(previousRsvps);
      error({ title: t("errors.error"), description: mapRsvpError(err) });
    } finally {
      setChangingRsvpId(null);
    }
  };

  const handleAddAttendance = async (
    target: { guestName?: string; playerId?: number; guestBalanceHint?: GuestBalanceHint },
    status: RsvpStatus
  ) => {
    const displayName =
      target.playerId != null
        ? (playerNameById.get(target.playerId) ?? `Player ${target.playerId}`)
        : (target.guestName ?? "");

    if (!displayName) return;

    if (!useLiveRsvpData) {
      if (target.guestName) {
        setMockGuests([...mockGuests, target.guestName]);
      } else if (target.playerId != null) {
        setMockManagedPlayerRsvps((prev) => ({ ...prev, [target.playerId!]: status }));
      }
      success({
        title: target.guestName ? t("v2.home.guestAdded") : t("v2.home.rsvpSaved"),
        description: target.guestName
          ? t("v2.home.guestAddedMock", { name: displayName })
          : t("v2.home.adminRsvpUpdated", {
              name: displayName,
              status: rsvpStatusLabel(status),
            }),
      });
      return;
    }

    const pendingId =
      target.playerId != null ? `player-${target.playerId}` : `guest-${target.guestName}`;
    const previousRsvps = rsvps;

    try {
      setChangingRsvpId(pendingId);

      const actor =
        target.playerId != null
          ? { playerId: target.playerId, addedByUserId: currentUserId ?? undefined }
          : {
              guestName: target.guestName,
              guestBalanceHint: target.guestBalanceHint ?? "unknown",
              addedByUserId: currentUserId ?? undefined,
            };

      const { appliedStatus, rsvps: updatedRsvps } = await applyRsvpChange(
        upcomingEvent,
        status,
        actor,
        {
          maxReserves: groupSettings?.max_reserves,
          minConfirmPct: groupSettings?.min_confirm_pct,
        }
      );

      if (target.guestName) {
        success({
          title: t("v2.home.guestAdded"),
          description:
            appliedStatus === "reserve"
              ? t("v2.home.guestAddedReserve", { name: displayName })
              : t("v2.home.guestAddedDesc", { name: displayName }),
        });
      } else {
        success({
          title: t("v2.home.rsvpSaved"),
          description: t("v2.home.adminRsvpUpdated", {
            name: displayName,
            status: rsvpStatusLabel(appliedStatus),
          }),
        });
      }

      setRsvps(updatedRsvps);
    } catch (err: unknown) {
      setRsvps(previousRsvps);
      error({ title: t("errors.error"), description: mapRsvpError(err) });
    } finally {
      setChangingRsvpId(null);
    }
  };

  const handleDispute = async () => {
    if (!resultEvent?.match_id || !groupId || !currentUserId) return;
    try {
      setDisputing(true);
      await fileMatchResultDispute(
        resultEvent.match_id,
        groupId,
        resultEvent.id,
        currentUserId
      );
      await dispatchNotification({
        groupId,
        type: "result_disputed",
        eventId: resultEvent.id,
        actorUserId: currentUserId,
        title: t("v2.home.disputeNotificationTitle"),
        body: t("v2.home.disputeNotificationBody"),
        url: "/v2/home",
      });
      success({ title: t("v2.home.disputeSaved"), description: t("v2.home.disputeSavedDesc") });
      await loadData({ silent: true });
    } catch {
      error({ title: t("errors.error"), description: t("v2.home.disputeError") });
    } finally {
      setDisputing(false);
    }
  };

  const handleLogResult = async () => {
    if (phaseOverride !== "auto" || !resultEvent) {
      setPhaseOverride("completed");
      success({
        title: t("v2.home.resultSaved"),
        description: t("v2.home.resultLoggedMock"),
      });
      return;
    }

    try {
      const hasBothScores = resultScores.a !== "" && resultScores.b !== "";
      const hasWinner =
        resultWinner === "A" || resultWinner === "B" || resultWinner === "Draw";

      if (!hasBothScores && !hasWinner) {
        throw new Error(t("v2.home.invalidResult"));
      }

      let resultInput: Parameters<typeof logMatchResultForEvent>[1];
      if (hasBothScores) {
        const scoreA = parseInt(resultScores.a, 10);
        const scoreB = parseInt(resultScores.b, 10);
        if (isNaN(scoreA) || isNaN(scoreB)) {
          throw new Error(t("v2.home.invalidScores"));
        }
        resultInput = { scoreA, scoreB };
      } else {
        resultInput = { winner: resultWinner! };
      }

      if (!groupId) throw new Error("No group selected");

      if (goingPlayerIds.length === 0) {
        error({
          title: t("errors.error"),
          description: t("v2.home.noPlayersForMatch"),
        });
        return;
      }

      const match = await logMatchResultForEvent(
        resultEvent,
        resultInput,
        groupId,
        goingPlayerIds,
        { playerNameById, guestNameByRsvpId }
      );

      applyScoresFromMatch(match);
      const resultBody =
        match.scorea != null && match.scoreb != null
          ? t("v2.push.resultPublishedBody", {
              scoreA: match.scorea,
              scoreB: match.scoreb,
            })
          : t("v2.push.resultPublishedWinnerBody", {
              winner:
                match.winner === "A"
                  ? t("v2.home.wonTeamA")
                  : match.winner === "B"
                    ? t("v2.home.wonTeamB")
                    : t("v2.home.matchDraw"),
            });
      await dispatchNotification({
        groupId,
        type: "result_published",
        eventId: resultEvent.id,
        actorUserId: currentUserId,
        title: t("v2.push.resultPublishedTitle"),
        body: resultBody,
        url: "/v2/stats",
      });
      success({
        title: t("v2.home.resultSaved"),
        description: hasExistingResult
          ? t("v2.home.resultUpdatedDesc")
          : t("v2.home.resultSavedDesc"),
      });
      await loadData({ silent: true });
    } catch (err: unknown) {
      if (err instanceof Error && err.message === "NO_PLAYERS_FOR_MATCH") {
        error({ title: t("errors.error"), description: t("v2.home.noPlayersForMatch") });
        return;
      }
      const message = err instanceof Error ? err.message : t("rsvp.saveError");
      error({ title: t("errors.error"), description: message });
    }
  };

  const handleShareTeams = () => {
    const teamANames = confirmedTeamA.map((p) => p.name);
    const teamBNames = confirmedTeamB.map((p) => p.name);
    shareOrWhatsApp(
      formatTeamsShareMessage(teamANames, teamBNames, currentGroup?.name)
    );
  };

  const cycleShareMessageVariant = activePhase === "poll_open" ? "poll" : "cycle";

  const cycleShareAction = showCycleShare ? (
    <HomeCycleShareButton
      shareUrl={cycleShare.url}
      isAdmin={isAdmin}
      onOpenShare={isAdmin ? () => setCycleShareDialogOpen(true) : undefined}
    />
  ) : undefined;

  const goingPlayersForLineup = useMemo((): LineupPlayer[] =>
    goingRsvps.flatMap((rsvp) => {
      if (rsvp.player_id != null) {
        const roster = rosterById.get(rsvp.player_id);
        return [
          {
            key: `player-${rsvp.player_id}`,
            name: formatRsvpName(rsvp),
            playerId: rsvp.player_id,
            balanceScore: roster ? playerBalanceScore(roster) : groupAvgRating,
          },
        ];
      }
      if (rsvp.guest_name) {
        const cached = getGuestProfile(guestProfiles, rsvp.guest_name);
        return [
          {
            key: `guest-${rsvp.id}`,
            name: rsvp.guest_name,
            guestRsvpId: rsvp.id,
            guestName: rsvp.guest_name,
            balanceScore: computeGuestBalanceScore(groupAvgRating, {
              hint: (rsvp.guest_balance_hint as GuestBalanceHint | null) ?? "unknown",
              cached,
            }),
          },
        ];
      }
      return [];
    }),
    [goingRsvps, playerNameById, rosterById, guestProfiles, groupAvgRating]
  );

  const lineupEventId =
    upcomingEvent?.id ?? (!isAutoPhase && isRsvpPhase ? MOCK_LINEUP_EVENT_ID : undefined);

  const handlePublishLineup = async (teamA: LineupMember[], teamB: LineupMember[]) => {
    if (!upcomingEvent) {
      if (!isAutoPhase) {
        setMockPublishedLineup({
          teamA: teamA.map((m) => m.playerId ?? 0).filter((id) => id > 0),
          teamB: teamB.map((m) => m.playerId ?? 0).filter((id) => id > 0),
        });
        success({
          title: t("v2.home.lineupPublished"),
          description: t("v2.home.lineupMockPublishedDesc"),
        });
      }
      return;
    }
    try {
      setPublishingLineup(true);
      await saveEventLineup(upcomingEvent.id, teamA, teamB);
      success({
        title: t("v2.home.lineupPublished"),
        description: t("v2.home.lineupPublishedDesc"),
      });
      await loadData({ silent: true });
    } catch {
      error({ title: t("errors.error"), description: t("rsvp.saveError") });
    } finally {
      setPublishingLineup(false);
    }
  };

  const handleConfirmEnterResult = async () => {
    if (!upcomingEvent) {
      if (!isAutoPhase) {
        setEnterResultOpen(false);
        setPhaseOverride("post_kickoff");
        success({
          title: t("v2.home.enterResultDone"),
          description: t("v2.home.enterResultDoneDesc"),
        });
      }
      return;
    }
    try {
      setStartingPostKickoff(true);
      await startPostKickoff(upcomingEvent.id);
      setEnterResultOpen(false);
      success({
        title: t("v2.home.enterResultDone"),
        description: t("v2.home.enterResultDoneDesc"),
      });
      await loadData({ silent: true });
    } catch (err) {
      const message =
        err instanceof Error && err.message === "LINEUP_NOT_PUBLISHED"
          ? t("v2.home.lineupRequiredForResult")
          : t("rsvp.saveError");
      error({ title: t("errors.error"), description: message });
    } finally {
      setStartingPostKickoff(false);
    }
  };

  if (loading && (phaseOverride === "auto" || isPublicHome)) {
    return <PageLoader label={t("common.loading", "Loading...")} />;
  }

  if (isPublicHome && isTerminalPublicLinkStatus(publicLinkStatus)) {
    return (
      <div className="flex justify-center py-8">
        <PublicCycleStatusScreen
          status={publicLinkStatus}
          groupName={currentGroup?.name}
          lastCompleted={lastCompleted}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {showDropBelowMinAlert && (
        <HomeAtRiskAlert
          needed={playersNeeded}
          going={confirmation.goingCount}
          min={confirmation.minPlayers}
        />
      )}

      <HomeWeekTracker current={activePhase} />

      {isRealAdmin && (
        <HomeAdminPhaseHint
          phase={activePhase}
          pollV2={showPollV2Ui ? activePollV2 : null}
          pollOpen={activePhase === "poll_open" && Boolean(activePollV2 ?? activePoll)}
        />
      )}

      {isPublicHome &&
        publicResultOverdue &&
        activePhase === "post_kickoff" &&
        !hasExistingResult && (
          <div
            className="rounded-xl border border-amber-400/60 bg-amber-400/10 px-4 py-3 text-sm text-foreground/90"
            role="status"
          >
            <p className="font-semibold">{t("v2.home.publicResultOverdueTitle")}</p>
            <p className="mt-1 text-muted-foreground">{t("v2.home.publicResultOverdueDesc")}</p>
          </div>
        )}

      {activePhase === "poll_inactive" && (
        <HomeIdleCard
          isAdmin={isAdmin}
          readOnly={isPublicHome}
          selectedDates={userAvailabilityDates}
          availabilitySummary={availabilitySummary}
          pendingDates={pendingAvailabilityDates}
          openingPoll={creatingPoll}
          onToggleDay={handleToggleAvailability}
          onOpenPoll={() => setPollModalOpen(true)}
        />
      )}

      {activePhase === "poll_open" && showPollV2Ui && (activePollV2 || !isAutoPhase) && activePollV2 && (
        <HomePollVotingCard
          poll={activePollV2}
          isAdmin={isAdmin}
          closingPoll={closingPoll}
          confirming={confirmingPoll}
          onVote={handleVoteSlots}
          onConfirm={handleConfirmPollSlot}
          onCancel={handleCancelPoll}
          headerAction={cycleShareAction}
        />
      )}

      {activePhase === "poll_open" && !showPollV2Ui && (activePoll || !isAutoPhase) && (
        <HomePollCard
          options={pollOptionsVM}
          closingPoll={closingPoll}
          canClose={useMockPollOptions || activePoll != null}
          isAdmin={isAdmin}
          onVote={handleVote}
          onClose={handleClosePoll}
          headerAction={cycleShareAction}
        />
      )}

      {!showPollV2Ui && (
      <Dialog open={tiebreakOpen} onOpenChange={setTiebreakOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("v2.home.tiebreakTitle")}</DialogTitle>
            <DialogDescription>{t("v2.home.tiebreakDescription")}</DialogDescription>
          </DialogHeader>
          <RadioGroup
            value={tiebreakSelectedId ?? undefined}
            onValueChange={setTiebreakSelectedId}
            className="py-2"
          >
            {tiebreakOptions.map((opt) => (
              <Label
                key={opt.id}
                htmlFor={`tiebreak-${opt.id}`}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors min-h-[44px] ${
                  tiebreakSelectedId === opt.id
                    ? "border-soccer-primary bg-soccer-primary/10"
                    : "border-border hover:border-soccer-primary/50"
                }`}
              >
                <RadioGroupItem value={opt.id} id={`tiebreak-${opt.id}`} />
                <span className="font-semibold">{formatOptionLabel(opt)}</span>
              </Label>
            ))}
          </RadioGroup>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTiebreakOpen(false)}
              className="min-h-[44px]"
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleConfirmTiebreak}
              disabled={!tiebreakSelectedId || closingPoll}
              className="bg-soccer-primary text-black hover:bg-soccer-primary/90 font-bold min-h-[44px]"
            >
              {t("v2.home.tiebreakConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      {isRsvpPhase && (
        <HomeCollectingCard
          isAdmin={isAdmin}
          canManageRsvp={canManageRsvp}
          canAddGuest={canAddGuest}
          weatherSummary={weatherSummary}
          relativeDate={eventRelativeDate}
          location={eventLocation}
          maxPlayers={maxPlayers}
          rsvpStatus={useLiveRsvpData ? currentUserRsvpStatus : mockRsvpStatus}
          onRsvp={handleRsvp}
          goingCount={useLiveRsvpData ? confirmation.goingCount : mockGoingCount}
          minPlayers={confirmation.minPlayers}
          confirmed={
            useLiveRsvpData
              ? confirmation.confirmed
              : mockGoingCount >= confirmation.minPlayers
          }
          minConfirmPct={displayMinConfirmPct}
          unrespondedPlayers={canManageRsvp ? unrespondedPlayers : []}
          onAddAttendance={canAddGuest || canManageRsvp ? handleAddAttendance : undefined}
          going={
            useLiveRsvpData
              ? goingRsvps.map(toRosterEntry)
              : mockRosterEntries.going
          }
          maybe={
            useLiveRsvpData
              ? maybeRsvps.map(toRosterEntry)
              : mockRosterEntries.maybe
          }
          out={
            useLiveRsvpData
              ? outRsvps.map(toRosterEntry)
              : mockRosterEntries.out
          }
          reserve={
            useLiveRsvpData
              ? reserveRsvps.map(toRosterEntry)
              : mockRosterEntries.reserve
          }
          onAdminRsvpChange={canManageRsvp ? handleAdminRsvpChange : undefined}
          changingRsvpId={changingRsvpId}
          hasPublishedLineup={collectingHasPublishedLineup}
          teamA={confirmedTeamA}
          teamB={confirmedTeamB}
          currentUserTeam={currentUserTeam}
          eventId={lineupEventId}
          goingPlayers={useLiveRsvpData ? goingPlayersForLineup : mockGoingPlayersForLineup}
          currentUserPlayerId={currentUserPlayerId}
          publishingLineup={publishingLineup}
          onPublishLineup={isAdmin ? handlePublishLineup : undefined}
          onShareWhatsApp={handleShareTeams}
          onEnterResult={isAdmin && collectingHasPublishedLineup ? () => setEnterResultOpen(true) : undefined}
          enteringResult={startingPostKickoff}
          onCancelMatch={isAdmin ? () => setCancelMatchOpen(true) : undefined}
          cancellingMatch={cancellingMatch}
          headerAction={cycleShareAction}
        />
      )}

      <CycleShareDialog
        open={cycleShareDialogOpen}
        onOpenChange={setCycleShareDialogOpen}
        shareUrl={cycleShare.url}
        ready={cycleShare.ready}
        groupName={currentGroup?.name}
        settingUp={settingUpCycleShare}
        sharePassword={cycleSharePasswordMemory}
        messageVariant={cycleShareMessageVariant}
        onSetupPassword={handleCycleShareSetupPassword}
      />

      <ConfirmationModal
        isOpen={cancelMatchOpen}
        onClose={() => setCancelMatchOpen(false)}
        onConfirm={handleConfirmCancelMatch}
        title={t("v2.home.cancelMatchTitle")}
        message={t("v2.home.cancelMatchConfirm")}
        confirmText={cancellingMatch ? t("common.loading") : t("v2.home.cancelMatch")}
        cancelText={t("common.cancel")}
        isDestructive
        isProcessing={cancellingMatch}
      />

      <ConfirmationModal
        isOpen={enterResultOpen}
        onClose={() => setEnterResultOpen(false)}
        onConfirm={handleConfirmEnterResult}
        title={t("v2.home.enterResultTitle")}
        message={t("v2.home.enterResultConfirm")}
        confirmText={startingPostKickoff ? t("common.loading") : t("v2.home.enterResult")}
        cancelText={t("common.cancel")}
        isProcessing={startingPostKickoff}
      />

      {activePhase === "post_kickoff" && pollsV2 && (
        <HomePostMatchCard
          mode={hasExistingResult ? "display" : isAdmin ? "edit" : "pending"}
          isAdmin={isAdmin}
          scoreA={resultScores.a}
          scoreB={resultScores.b}
          winner={resultWinner}
          onWinnerChange={setResultWinner}
          displayScoreA={completedScoreA}
          displayScoreB={completedScoreB}
          winnerLabel={
            hasExistingResult && completedWinner
              ? completedWinner === "A"
                ? t("v2.home.wonTeamA")
                : completedWinner === "B"
                  ? t("v2.home.wonTeamB")
                  : t("v2.home.matchDraw")
              : undefined
          }
          onScoreAChange={(v) => setResultScores({ ...resultScores, a: v })}
          onScoreBChange={(v) => setResultScores({ ...resultScores, b: v })}
          onLogResult={handleLogResult}
          hasExistingResult={hasExistingResult}
          canDispute={!isAdmin && hasExistingResult}
          onDispute={handleDispute}
          disputing={disputing}
          showStaleBanner={showStaleResultBanner}
          onSkipStale={() => setSkippedStaleResult(true)}
          skipped={skippedStaleResult}
          onStartNextWeek={() => (isAdmin ? setPollModalOpen(true) : undefined)}
          startingNextWeek={creatingPoll}
          onCancelMatch={isAdmin ? () => setCancelMatchOpen(true) : undefined}
          cancellingMatch={cancellingMatch}
          historySlot={renderResultHistory()}
          commentsSlot={renderEventComments()}
        />
      )}

      {activePhase === "post_kickoff" && !pollsV2 && (
        <HomePostKickoffCard
          isAdmin={isAdmin}
          scoreA={resultScores.a}
          scoreB={resultScores.b}
          winner={resultWinner}
          onScoreAChange={(v) => setResultScores({ ...resultScores, a: v })}
          onScoreBChange={(v) => setResultScores({ ...resultScores, b: v })}
          onWinnerChange={setResultWinner}
          hasExistingResult={hasExistingResult}
          onLogResult={handleLogResult}
          canDispute={!isAdmin && hasExistingResult}
          onDispute={handleDispute}
          disputing={disputing}
          showStaleBanner={showStaleResultBanner}
          onSkipStale={() => setSkippedStaleResult(true)}
          skipped={skippedStaleResult}
          onStartNextWeek={() => (isAdmin ? setPollModalOpen(true) : undefined)}
          startingNextWeek={creatingPoll}
          onCancelMatch={isAdmin ? () => setCancelMatchOpen(true) : undefined}
          cancellingMatch={cancellingMatch}
          historySlot={renderResultHistory()}
          commentsSlot={renderEventComments()}
        />
      )}

      {activePhase === "completed" && pollsV2 && (
        <HomePostMatchCard
          mode="completed_recap"
          isAdmin={isAdmin}
          scoreA={resultScores.a}
          scoreB={resultScores.b}
          winner={resultWinner}
          onWinnerChange={setResultWinner}
          displayScoreA={completedScoreA}
          displayScoreB={completedScoreB}
          winnerLabel={
            completedWinner === "A"
              ? t("v2.home.wonTeamA")
              : completedWinner === "B"
                ? t("v2.home.wonTeamB")
                : completedWinner === "Draw"
                  ? t("v2.home.matchDraw")
                  : t("v2.home.resultPending")
          }
          recapDateLabel={recapDateLabel}
          onScoreAChange={(v) => setResultScores({ ...resultScores, a: v })}
          onScoreBChange={(v) => setResultScores({ ...resultScores, b: v })}
          onLogResult={handleLogResult}
          hasExistingResult={hasExistingResult}
          showLoopReset={showLoopReset}
          onStartNextWeek={() => (isAdmin ? setPollModalOpen(true) : undefined)}
          startingNextWeek={creatingPoll}
          teaserLines={teaserLines}
          historySlot={renderResultHistory()}
          commentsSlot={renderEventComments()}
        />
      )}

      {activePhase === "completed" && !pollsV2 && (
        <HomeCompletedCard
          isAdmin={isAdmin}
          recapDateLabel={recapDateLabel}
          winner={completedWinner}
          scoreA={completedScoreA}
          scoreB={completedScoreB}
          teaserLines={teaserLines}
          showEditResult={isAutoPhase && isAdmin && Boolean(resultEvent)}
          editScoreA={resultScores.a}
          editScoreB={resultScores.b}
          editWinner={resultWinner}
          onEditScoreAChange={(v) => setResultScores({ ...resultScores, a: v })}
          onEditScoreBChange={(v) => setResultScores({ ...resultScores, b: v })}
          onEditWinnerChange={setResultWinner}
          onLogResult={handleLogResult}
          showLoopReset={showLoopReset}
          onStartNextWeek={() => (isAdmin ? setPollModalOpen(true) : undefined)}
          startingNextWeek={creatingPoll}
          historySlot={renderResultHistory()}
          commentsSlot={renderEventComments()}
        />
      )}
      {showDevPanel && (
        <HomeDevPanel
          phaseOverride={phaseOverride}
          roleOverride={devRoleOverride}
          onSelectAuto={() => {
            setPhaseOverride("auto");
            loadData({ silent: true });
          }}
          onSelectPhase={(p) => setPhaseOverride(p)}
          onSelectRole={setDevRoleOverride}
        />
      )}
      <PollCreationDialog
        open={pollModalOpen}
        onOpenChange={setPollModalOpen}
        availability={availabilityMap}
        creating={creatingPoll}
        groupName={currentGroup?.name}
        onConfirm={handleCreatePollV2}
      />
    </div>
  );
}
